import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { AccommodationCategoryDto } from '../accommodation-category/accommodation-category.model';
import { CampsiteDetailDto } from '../campsite/campsite.model';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private readonly metaService = inject(Meta);
  private readonly titleService = inject(Title);

  updateHomeSeo() {
    this.titleService.setTitle('WannaCamp | Prenota i migliori campeggi');

    // Standard Meta Tags
    this.metaService.addTag({
      name: 'description',
      content:
        'Find and book the best campsites easily with WannaCamp. Explore top-rated camping spots and plan your perfect outdoor adventure.',
    });

    this.metaService.addTag({
      name: 'keywords',
      content:
        'camping, campsite booking, outdoor adventure, travel, nature, WannaCamp',
    });

    // Open Graph Meta Tags (for social media sharing)
    this.metaService.addTag({
      property: 'og:title',
      content: 'WannaCamp | Prenota i migliori campeggi',
    });

    this.metaService.addTag({
      property: 'og:description',
      content:
        'Discover and reserve amazing campsites effortlessly with WannaCamp. Plan your next outdoor getaway today!',
    });

    this.metaService.addTag({
      property: 'og:image',
      content: 'path/to/your/image.png',
    });

    this.metaService.addTag({
      property: 'og:url',
      content: 'https://www.wannacamp.com',
    });

    this.metaService.addTag({
      property: 'og:type',
      content: 'website',
    });
  }

  updateCampsiteSeo(campsite: CampsiteDetailDto) {
    if (!campsite) {
      return;
    }

    const title = `${campsite.name} - WannaCamp`;
    const description =
      campsite.description || 'Prenota il miglior campeggio con WannaCamp.';
    const imageUrl = campsite.images?.length
      ? campsite.images[0]
      : 'path/to/default-image.png';
    const url = `https://www.wannacamp.com/campsites/${campsite.slug}`;

    this.titleService.setTitle(title);

    // Standard Meta Tags
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({
      name: 'keywords',
      content: `camping, campeggio, natura, viaggio, WannaCamp, ${campsite.name}`,
    });

    // Open Graph Meta Tags
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({
      property: 'og:description',
      content: description,
    });
    this.metaService.updateTag({ property: 'og:image', content: imageUrl });
    this.metaService.updateTag({ property: 'og:url', content: url });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });

    // Twitter Meta Tags
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({
      name: 'twitter:description',
      content: description,
    });
    this.metaService.updateTag({ name: 'twitter:image', content: imageUrl });

    // Location Meta Tags
    if (campsite.position) {
      const location = `${campsite.position.city}, ${campsite.position.state}, ${campsite.position.country}`;
      this.metaService.updateTag({ name: 'geo.placename', content: location });
      this.metaService.updateTag({
        name: 'geo.position',
        content: `${campsite.position.latitude};${campsite.position.longitude}`,
      });
      this.metaService.updateTag({
        name: 'ICBM',
        content: `${campsite.position.latitude}, ${campsite.position.longitude}`,
      });
    }
  }

  updateAccommodationSeo(accommodation: AccommodationCategoryDto) {
    throw new Error('Method not implemented.');
  }
}
